<template>
  <v-container fluid class="pa-0">
    <v-row>
      <v-col align="right" class="px-0 pt-2 pb-0">
        <div v-if="companyLogoHref === undefined">
          <Icon
            icon-color="white"
            icon="mdi-file-image-plus"
            :small="false"
            :tooltipText="$t('uploadCompanyLogo')"
            xLarge
            @icon-clicked="openUploadDialog"
            margin="mt-n2 mr-2"/>
          </div>
          <div v-else class="logo-container">
            <img
            :src="companyLogoHref"
            style="width: 75px; height: 75px; width: 100%; border-radius: 4px;"
            class="image"
            @click="deleteLogoConfirmation = true"
            alt="Logo">
            <div class="delete-icon">
              <v-icon large @click="deleteLogoConfirmation = true">mdi-delete</v-icon>
            </div>
          </div>
      </v-col>
    </v-row>
    <input
      ref="uploader"
      class="d-none"
      type="file"
      @change="fileChanged"/>
    <v-dialog v-model="deleteLogoConfirmation" width="400px">
      <ConfirmDialog
        :title="$t('deleteCompanyLogo')"
        :body="$t('deleteCompanyLogoBody')"
        :confirmText="$t('deleteLogo')"
        @confirm="deleteLogo()"
        @cancel="deleteLogoConfirmation = false"/>
    </v-dialog>
    <v-dialog v-model="logoUploadDialog" width="400px">
      <v-card>
        <v-card-title class="primary white--text">
          <v-row>
            <v-col>
              <span>{{ $t('companyLogo') }}</span>
            </v-col>
            <v-col cols="auto">
              <v-icon @click="closeUploadDialog" large color="tertiary">mdi-close</v-icon>
            </v-col>
          </v-row>
        </v-card-title>
        <v-card-text class="pt-6">
          <v-row dense>
            <v-col>
              <v-btn @click="changeLogo" color="primary" style="width: 100%">
                <v-icon>mdi-file-image-plus</v-icon>
                <span>{{ $t('chooseFile') }}</span>
              </v-btn>
            </v-col>
            <v-col cols="auto">
              <v-tooltip color="info" bottom min-width="340">
                <template #activator="{ on }">
                  <v-icon v-on="on" color="info" large>mdi-help-circle-outline</v-icon>
                </template>
                <v-row dense>
                  <v-col cols="12" v-for="warning in warnings" :key="warning.text">
                    <span class="white--text subtitle-1">{{ warning.text }}</span>
                  </v-col>
                </v-row>
              </v-tooltip>
            </v-col>
            <v-col cols="12" v-for="warning in visibleWarnings" :key="warning.text">
              <span class="error--text text-right caption">{{ warning.text }}</span>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<style>
  .logo-container {
    display: inline-block;
    position: relative
  }
  .logo-container:hover .image{
    opacity: 0.5;
    cursor: pointer;
  }
  .logo-container:hover .delete-icon{
    bottom: 0;
    right: 0;
    cursor: pointer;
    display: block
  }
  .logo-container .delete-icon {
    display: none;
    position: absolute
  }
</style>

<script>
import { companyLogoClient } from '@/utils/CompanyLogo'
const LOGO_MIN_DIMENSION = 60
const LOGO_MAX_SIZE = '1MB'
export default {
  name: 'CompanyLogo',

  props: {
    companyInfo: {
      type: Object,
      required: true
    },
    allowEdit: {
      type: Boolean,
      required: false,
      default: false
    }
  },

  components: {
    Icon: () => import('@/components/helper/Icon.vue'),
    ConfirmDialog: () => import('@/components/helper/ConfirmDialog.vue')
  },

  data: () => ({
    companyLogoHref: undefined,
    selectedFile: undefined,
    isSelecting: false,
    deleteLogoConfirmation: false,
    allowedLogoMimes: undefined,
    logoUploadDialog: false,

    showSizeWarning: false,
    showTypeWarning: false,
    showDimensionWarning: false,
    showRatioWarning: false
  }),

  async created () {
    if (this.companyInfo.companyInfoId) this.companyLogoHref = await companyLogoClient.getCompanyLogo(this.companyInfo.companyInfoId)
    this.allowedLogoMimes = new Set([
      'image/png',
      'image/jpeg'
    ])
  },

  watch: {
    companyInfo: {
      async handler (val) {
        if (val.companyInfoId) this.companyLogoHref = await companyLogoClient.getCompanyLogo(this.companyInfo.companyInfoId)
      }
    }
  },

  computed: {
    warnings () {
      return [
        {
          text: this.$t('mustBeSmallerThan', { size: LOGO_MAX_SIZE }),
          visible: this.showSizeWarning
        },
        {
          text: this.$t('mustBePngOrJpg'),
          visible: this.showTypeWarning
        },
        {
          text: this.$t('mustBeAtLeastDimension', { dimension: '60px' }),
          visible: this.showDimensionWarning
        },
        {
          text: this.$t('mustHaveAspectRatioBetween', { first: '3:4', second: '4:3' }),
          visible: this.showRatioWarning
        }
      ]
    },

    visibleWarnings () {
      return this.warnings.filter(w => w.visible)
    }
  },

  methods: {
    changeLogo () {
      this.isSelecting = true
      window.addEventListener('focus', () => {
        this.isSelecting = false
      }, { once: true })
      this.$refs.uploader.click()
    },

    fileChanged (e) {
      this.selectedFile = e.target.files[0]
      this.uploadToBlob()
      this.isSelecting = false
      this.$refs.uploader.value = ''
    },

    async uploadToBlob () {
      const validationResult = await this.validateLogo(this.selectedFile)
      if (!validationResult) return
      await companyLogoClient.uploadCompanyLogo(this.companyInfo.companyInfoId, this.selectedFile)
      this.companyLogoHref = await companyLogoClient.getCompanyLogo(this.companyInfo.companyInfoId)
    },

    async deleteLogo () {
      await companyLogoClient.deleteCompanyLogo(this.companyLogoHref)
      this.companyLogoHref = undefined
      this.deleteLogoConfirmation = false
    },

    async validateLogo (file) {
      const limit = 1000 * 1000 // 1MB
      if (file.size > limit) {
        this.setSnackError(this.$t('fileTooLargeSnack', { limit: '1MB' }))
        this.selectedFile = undefined
        this.showSizeWarning = true
        return false
      }
      if (!this.allowedLogoMimes.has(file.type)) {
        this.setSnackError(this.$t('fileMimeNotAllowedSnack'))
        this.selectedFile = undefined
        this.showTypeWarning = true
        return false
      }
      const dimensions = await companyLogoClient.getLogoDimensions(await companyLogoClient.convertImageToDataUrl(file))
      if (dimensions.width < LOGO_MIN_DIMENSION || dimensions.height < LOGO_MIN_DIMENSION) {
        this.setSnackError(this.$t('imageTooSmallSnack'))
        this.selectedFile = undefined
        this.showDimensionWarning = true
        return false
      }
      if ((Math.abs(dimensions.width - dimensions.height) / Math.max(dimensions.width, dimensions.height)) > 0.25) {
        this.setSnackError(this.$t('imageNotSquareSnack'))
        this.selectedFile = undefined
        this.showRatioWarning = true
        return false
      }
      this.closeUploadDialog()
      return true
    },

    openUploadDialog () {
      this.logoUploadDialog = true
    },

    closeUploadDialog () {
      this.logoUploadDialog = false
    }
  }
}
</script>
